import { App, defineComponent, getCurrentInstance, h, nextTick, reactive } from 'vue';
import { findParentShadowRoot } from './query-element.util';
import { MarketConfig } from './config-service';

const getShadowRootOrHeadElem = (el: HTMLElement) => {
  const shadowRoot = findParentShadowRoot(el);
  return shadowRoot ?? document.head;
};

// Component wrapper factory function to solve 2 issues that Vue3 has:
// 1. child component styles are not injected into shadowRoot (https://github.com/vuejs/core/issues/4662)
// 2. no way to provide a custom "app" when using Vue's `defineCustomElement` function
// Once those issues are solved by Vue itself this wrapper can be removed.
export const createWcWrapper = (component: ReturnType<typeof defineComponent>, app: App, config: MarketConfig) =>
  defineComponent({
    name: 'VueWcWrapper',
    // Do not delete empty props object here!
    // Vue3's custom element wrapper requires it to be set, otherwise runtime errors are thrown.
    props: {},
    setup(_, ctx) {
      // Mixin to inject component styles into shadowRoot as workaround
      app.mixin({
        mounted() {
          this.__styles = [] as HTMLStyleElement[];

          const insertStyles = async (styles: string[], id: string) => {
            if (styles?.length) {
              await nextTick();
              const elem = getShadowRootOrHeadElem(this.$el);

              // Check if style already exists
              let style = elem.querySelector<HTMLStyleElement>(`[data-vue-comp="${id}"]`);

              if (!style) {
                style = document.createElement('style');
                style.setAttribute('data-vue-comp', id);
                style.innerText = styles.join('').replace(/\n/g, '');
                this.__styles.push(style);
                elem.prepend(style);
              }
            }
          };

          // load own styles
          insertStyles(this.$?.type.styles, this.$?.type.name);

          // load styles of child components
          if (this.$options.components) {
            for (const comp of Object.values(this.$options.components) as any) {
              insertStyles(comp.styles, comp.name);
            }
          }
        },
        unmounted() {
          this.__styles.forEach((style: HTMLStyleElement) => style.remove());
        },
      });

      const instance = getCurrentInstance();

      // Assign app context to this instances context, so that we get
      // access to all plugins & global Vue methods inside components.
      // Reason is that we cannot provide our own app context to Vue's
      // `defineCustomElement` function, it creates its own internal app context.
      if (instance) {
        Object.assign(instance.appContext, app._context);
      }

      return () => h(component, { ...ctx.attrs, config });
    },
  });
